import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon, AccessTime, LocationOn, Person, Camera } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { NotificationData } from './NotificationBell';

interface NotificationDetailModalProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationData | null;
}

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  open,
  onClose,
  notification,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'notificationDetailModal' });

  if (!notification) return null;

  const getAlertLevelColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'error';
      case 'yellow':
        return 'warning';
      case 'green':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #764ba2 100%)`,
          color: 'white',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {notification.policyName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {/* Alert Level Badge */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={`${t('alertLevel')} ${notification.alertLevel?.toUpperCase() ?? t('infoFallback')}`}
            color={getAlertLevelColor(notification.alertLevel) as any}
            sx={{ fontWeight: 600, px: 1 }}
          />
        </Box>

        {/* Detection Image */}
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'divider',
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {notification.imageUrl && notification.coordinates ? (
            <img
              src={notification.imageUrl}
              alt={t('detectionAlt', { childName: notification.childName })}
              className="img-fluid"
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
            />
          ) : (
            <img
              src={notification.imageUrl}
              alt={t('detectionAlt', { childName: notification.childName })}
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorMsg = document.createElement('div');
                errorMsg.style.padding = '40px';
                errorMsg.style.color = '#999';
                errorMsg.style.textAlign = 'center';
                errorMsg.textContent = t('imageNotAvailable');
                target.parentElement?.appendChild(errorMsg);
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Detection Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Person sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('child')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.childName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('childIdPrefix')} {notification.childId}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <LocationOn sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('classroom')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.classroom}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Camera sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('camera')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.camera}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <AccessTime sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('detectionTime')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.detectionTime}
                  </Typography>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Additional Info Box */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{t('policyLabel')}</strong> {t('policyExplanation', { policyName: notification.policyName })}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationDetailModal;
